// Every network call this feature makes, all through the one client T-03 owns (`@/lib/api/client`)
// — never a fetch of its own, so the CSRF header (DESIGN.md §8.1.1) is never re-implemented here.
// Each function returns the raw `{ data, error, response }` openapi-fetch gives back rather than
// throwing, because the sign-in form needs to branch on `response.status` (401 vs 429 vs 415) with
// different copy for each — collapsing that into one thrown error would lose the distinction.
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import { api } from '../../lib/api/client';
import type { components } from '../../lib/api/client';

export type LoginRequest = components['schemas']['LoginRequest'];
export type RegisterRequest = components['schemas']['RegisterRequest'];
export type ActivationRequest = components['schemas']['ActivationRequest'];
export type AuthenticatedUser = components['schemas']['AuthenticatedUser'];
export type Profile = components['schemas']['Profile'];
export type Session = components['schemas']['Session'];

/**
 * DESIGN.md §8.1, user decision 2026-09-15: the TOTP code travels in the **same** `auth_login`
 * body as the password. `totp_code` is only ever included when the caller actually typed one —
 * an empty string would fail the contract's `^[0-9]{6}$` pattern with 400 instead of taking the
 * password-only path a non-staff account uses.
 */
export function login(input: {
  email: string;
  password: string;
  door: 'investor' | 'staff';
  totpCode?: string;
}) {
  const body: LoginRequest = {
    email: input.email,
    password: input.password,
    door: input.door,
    ...(input.totpCode ? { totp_code: input.totpCode } : {}),
  };
  return api.POST('/auth/login', { body });
}

export function registerAccount(body: RegisterRequest) {
  return api.POST('/auth/register', { body });
}

export function activateAccount(body: ActivationRequest) {
  return api.POST('/auth/activation', { body });
}

export function logout() {
  return api.POST('/auth/logout');
}

export function getProfile() {
  return api.GET('/me');
}

export function listSessions() {
  return api.GET('/auth/sessions');
}

export function revokeSession(sessionId: string) {
  return api.DELETE('/auth/sessions/{sessionId}', { params: { path: { sessionId } } });
}
